<div class="map-container">
  <div class="map-main-area">
    <!-- Map Controls -->
    <div class="map-controls">
      <div class="map-controls-header">
        @if (loading) {
          <div class="loading-indicator">
            <i class="fas fa-spinner fa-spin"></i> Loading devices...
          </div>
        }

        <button
          type="button"
          class="refresh-button"
          (click)="manualRefresh()"
          [disabled]="loading"
          title="Refresh hardware map data"
          aria-label="Refresh hardware map"
          >
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>

      <div class="map-type-toggle">
        <button
          type="button"
          class="toggle-button"
          [ngClass]="{ active: mapViewMode === 'roadmap' }"
          (click)="setMapView('roadmap')"
          >
          Map
        </button>
        <button
          type="button"
          class="toggle-button"
          [ngClass]="{ active: mapViewMode === 'satellite' }"
          (click)="setMapView('satellite')"
          >
          Satellite
        </button>
      </div>

      <div class="device-toggle-panel">
        <!-- Global Lot & Level Selectors -->
        @if (parkingLots && parkingLots.length) {
          <div class="global-selectors">
            <div class="global-lot-selector">
              <label for="globalLotSelect">Lot:</label>
              <select
                id="globalLotSelect"
                [(ngModel)]="selectedParkingLotId"
                (ngModelChange)="onParkingLotChange()"
                >
                <option [ngValue]="null">ALL Lots</option>
                @for (lot of parkingLots; track lot) {
                  <option [ngValue]="lot.Id">
                    {{ lot.Name }}
                  </option>
                }
              </select>
            </div>
            @if (allLevels && allLevels.length) {
              <div class="global-level-selector">
                <label for="globalLevelSelect">Level:</label>
                <select
                  id="globalLevelSelect"
                  [(ngModel)]="selectedGlobalLevelNumber"
                  (ngModelChange)="onGlobalLevelChange()"
                  >
                  <option [ngValue]="null">ALL Levels</option>
                  @for (level of allLevels; track level) {
                    <option [ngValue]="level">
                      Level {{ level }}
                    </option>
                  }
                </select>
              </div>
            }
          </div>
        }

        <div class="space-status-legends">
          <div class="legend-column">
            <div class="legend-heading">Space Colors:</div>
            <div class="legend-row">
              <span class="legend-dot legend-occupied"></span>
              <span class="legend-label">Occupied</span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-available"></span>
              <span class="legend-label">Available</span>
            </div>
          </div>

          <div class="legend-divider"></div>

          <div class="legend-column">
            <div class="legend-heading">Device Status:</div>
            <div class="legend-row">
              <span class="legend-dot legend-online"></span>
              <span class="legend-label">Online</span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-warning"></span>
              <span class="legend-label">Warning</span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-offline"></span>
              <span class="legend-label">Offline</span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-unknown"></span>
              <span class="legend-label">No Assignment</span>
            </div>
          </div>
        </div>

        <div class="selection-panel">
          <button
            type="button"
            class="polygon-btn"
            (click)="togglePolygonDrawing()"
            >
            <i class="fas fa-draw-polygon"></i>
            {{ isPolygonDrawing ? 'Finish Selection' : 'Draw Selection Polygon' }}
          </button>

          @if (isPolygonDrawing || selectedDevices.length > 0) {
            <button
              type="button"
              class="polygon-cancel-btn"
              (click)="cancelPolygonDrawing()"
              >
              Cancel Polygon
            </button>
          }

          @if (hasCompletedPolygonSelection && selectedDevices.length === 0) {
            <div class="selected-devices-card">
              <div class="selected-devices-header">
                <i class="fas fa-clipboard-list"></i>
                <span>Selected Devices (0)</span>
              </div>
              <div class="selected-devices-body empty">
                No devices selected. Draw a polygon to select devices.
              </div>
            </div>
          }

          @if (hasCompletedPolygonSelection && selectedDevices.length > 0) {
            <div class="selected-devices-card">
              <div class="selected-devices-header">
                <i class="fas fa-clipboard-list"></i>
                <span>Selected Devices ({{ selectedDevices.length }})</span>
                <button
                  type="button"
                  class="export-btn"
                  (click)="exportSelectedDevices()"
                  >
                  Export
                </button>
              </div>
              <div class="selected-devices-body">
                <table class="selected-devices-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>ChipId</th>
                      <th>Type</th>
                      <th>Latitude</th>
                      <th>Longitude</th>
                      <th>Status</th>
                      <th>Occupancy</th>
                      <th>Last Check-In</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (d of selectedDevices; track d) {
                      <tr>
                        <td class="name">
                          {{ d.Name }}
                        </td>
                        <td class="chip">
                          @if (d.ChipId) {
                            <span>{{ d.ChipId }}</span>
                          } @else {
                            -
                          }
                        </td>
                        <td>{{ d.DeviceType }}</td>
                        <td class="coord">
                          {{ getDeviceLatitude(d) | number:'1.6-6' }}
                        </td>
                        <td class="coord">
                          {{ getDeviceLongitude(d) | number:'1.6-6' }}
                        </td>
                        <td>{{ d.Status || '-' }}</td>
                        <td>{{ d.OccupancyStatus || '-' }}</td>
                        <td>{{ d.LastCheckinOn || '-' }}</td>
                        <td class="actions">
                          @if (getDeviceConfigUrl(d); as configUrl) {
                            <a
                              [href]="configUrl"
                              class="config-link"
                              >
                              Configuration
                            </a>
                          }
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          }
        </div>

        <h4 class="panel-title-text">Toggle Device Types:</h4>

        <div class="device-toggle-grid">
          <label
            class="device-toggle"
            [class.is-disabled]="!visibleDeviceTypes['ParkingSpace']"
            (click)="toggleDeviceType('ParkingSpace')"
            >
            <span class="device-label sensors">
              <span class="device-icon device-icon--sensor">
                <i class="fas fa-draw-polygon"></i>
              </span>
              Spaces/Devices
            </span>
          </label>

          <label
            class="device-toggle"
            [class.is-disabled]="!visibleDeviceTypes['GuidanceLight']"
            (click)="toggleDeviceType('GuidanceLight')"
            >
            <span class="device-label lights">
              <span class="device-icon device-icon--light"></span>
              Lights
            </span>
          </label>

          <label
            class="device-toggle"
            [class.is-disabled]="!visibleDeviceTypes['CarCounter']"
            (click)="toggleDeviceType('CarCounter')"
            >
            <span class="device-label car-counters">
              <span class="device-icon device-icon--car-counter"></span>
              Car Counters
            </span>
          </label>

          <label
            class="device-toggle"
            [class.is-disabled]="!visibleDeviceTypes['Sign']"
            (click)="toggleDeviceType('Sign')"
            >
            <span class="device-label signs">
              <span class="device-icon device-icon--sign"></span>
              Signs
            </span>
          </label>

          <label
            class="device-toggle"
            [class.is-disabled]="!visibleDeviceTypes['Gateway']"
            (click)="toggleDeviceType('Gateway')"
            >
            <span class="device-label gateways">
              <span class="device-icon device-icon--gateway"></span>
              Gateways
            </span>
          </label>
        </div>

        <hr class="toggle-separator" />

        <div class="display-toggle-list">
          <label class="display-toggle">
            <input
              type="checkbox"
              name="showLotPolygons"
              [(ngModel)]="showParkingLotPolygons"
              (change)="onToggleParkingLotPolygons()"
              />
              <span class="display-label">Show lot outlines</span>
            </label>

            <label class="display-toggle">
              <input
                type="checkbox"
                name="showLotNames"
                [(ngModel)]="showLotNames"
                (change)="onToggleLotNames()"
                />
                <span class="display-label">Show lot names</span>
              </label>

              <label class="display-toggle">
                <input
                  type="checkbox"
                  name="showSpaceNames"
                  [(ngModel)]="showSpaceNames"
                  (change)="onToggleSpaceNames()"
                  />
                  <span class="display-label">Show space names</span>
                </label>

                <label class="display-toggle">
                  <input
                    type="checkbox"
                    name="showStatusDots"
                    [(ngModel)]="showStatusDots"
                    (change)="onToggleStatusDots()"
                    />
                    <span class="display-label">Show status dots</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Search Overlay -->
            <div class="search-overlay">
              <div class="search-container">
                <div class="search-input-wrapper">
                  <i class="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    class="search-input"
                    placeholder="Search devices by name or chip ID..."
                    [(ngModel)]="searchQuery"
                    (input)="onSearchQueryChange()"
                    autocomplete="off"
                    />
                    @if (searchQuery.length > 0) {
                      <button
                        class="clear-search-btn"
                        (click)="clearSearch()"
                        title="Clear search"
                        >
                        <i class="fas fa-times"></i>
                      </button>
                    }
                  </div>

                  <!-- Search Results Count -->
                  @if (showSearchResults) {
                    <div class="search-results-count">
                      <span>{{searchResults.length}} result(s) found</span>
                    </div>
                  }
                </div>
              </div>

              <!-- Google Map -->
              <div #mapContainer class="google-map"></div>

              <!-- Properties Panel (docked inside map, right side) -->
              @if (showDeviceInfo && selectedDevice) {
                <div class="properties-panel-wrapper">
                  <div class="properties-panel-background"></div>
                  <div class="properties-panel">
                    <div class="panel-header">
                      <div class="panel-title">
                        <i class="fas fa-info-circle"></i>
                        <h3>Device Properties</h3>
                      </div>
                      <button class="close-btn" (click)="closeDeviceInfo()" title="Close properties panel">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                    <div class="panel-content">
                      <!-- Device Header -->
                      <div class="device-header">
                        <div class="device-main">
                          <div class="device-name">{{selectedDevice.Name}}</div>
                          <div class="device-type-badge" [class]="'type-' + selectedDevice.DeviceType.toLowerCase()">
                            {{selectedDevice.DeviceType}}
                          </div>
                        </div>
                        <div class="device-actions">
                          @if (getDeviceConfigUrl(selectedDevice); as configUrl) {
                            <a
                              [href]="configUrl"
                              class="panel-config-link"
                              >
                              Configuration
                            </a>
                          }
                        </div>
                      </div>
                      <!-- Device Information -->
                      <div class="info-section">
                        <button
                          type="button"
                          class="info-section-header"
                          (click)="toggleDeviceInfoCollapsed()"
                          [attr.aria-expanded]="!deviceInfoCollapsed"
                          aria-controls="device-info-body"
                          >
                          <h4><i class="fas fa-cog"></i> Device Information</h4>
                          <i class="fas" [ngClass]="deviceInfoCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                        </button>
                        @if (!deviceInfoCollapsed) {
                          <div id="device-info-body" class="info-section-body">
                            <table class="device-info-table">
                              <tbody>
                                <tr>
                                  <th scope="row">Status</th>
                                  <td>
                                    <span [class]="getDeviceStatusClass(selectedDevice.Status)">
                                      <i class="fas fa-circle status-indicator"></i>
                                      {{selectedDevice.Status || 'Unknown'}}
                                    </span>
                                  </td>
                                </tr>
                                @if (selectedDevice.ChipId) {
                                  <tr>
                                    <th scope="row">Chip ID</th>
                                    <td>{{selectedDevice.ChipId}}</td>
                                  </tr>
                                }
                                @if (selectedDevice.LastCheckinOn) {
                                  <tr>
                                    <th scope="row">Last Check-In</th>
                                    <td>{{selectedDevice.LastCheckinOn}}</td>
                                  </tr>
                                }
                                @if (selectedDevice.GatewayChipId) {
                                  <tr>
                                    <th scope="row">Gateway Chip ID</th>
                                    <td>{{selectedDevice.GatewayChipId}}</td>
                                  </tr>
                                }
                                <tr>
                                  <th scope="row">Location</th>
                                  <td>{{selectedDevice.LocationString}}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        }
                      </div>
                      <!-- Parking Space Details -->
                      @if (selectedDevice.DeviceType === 'ParkingSpace') {
                        <div class="info-section">
                          <button
                            type="button"
                            class="info-section-header"
                            (click)="toggleSpaceDetailsCollapsed()"
                            [attr.aria-expanded]="!spaceDetailsCollapsed"
                            aria-controls="space-details-body"
                            >
                            <h4><i class="fas fa-car"></i> Space Details</h4>
                            <i class="fas" [ngClass]="spaceDetailsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                          </button>
                          @if (!spaceDetailsCollapsed) {
                            <div id="space-details-body" class="info-section-body">
                              @if (isLoadingDeviceDetail) {
                                <div class="loading-indicator">
                                  <i class="fas fa-spinner fa-spin"></i> Loading space information...
                                </div>
                              }
                              @if (!isLoadingDeviceDetail) {
                                @if (parkingSpaceDetail; as detail) {
                                  <div class="status-grid">
                                    <div class="status-card" [class.negative]="detail?.isOccupied" [class.positive]="detail?.isOccupied === false">
                                      <div class="status-label"></div>
                                      <div class="status-value">
                                        {{ detail?.isOccupied === true ? 'Occupied' : (detail?.isOccupied === false ? 'Available' : 'Unknown') }}
                                      </div>
                                    </div>
                                    @if (detail?.parkingSpaceTypeName) {
                                      <div class="status-card neutral">
                                        <div class="status-label">Space Type</div>
                                        <div class="status-value">{{detail?.parkingSpaceTypeName}}</div>
                                      </div>
                                    }
                                  </div>
                                  <table class="device-config-table">
                                    <tbody>
                                      @if (detail?.occupiedSpaces !== undefined && detail?.occupiedSpaces !== null) {
                                        <tr>
                                          <th scope="row">Occupied Spaces</th>
                                          <td>{{detail?.occupiedSpaces}}</td>
                                        </tr>
                                      }
                                      @if (detail?.availableSpaces !== undefined && detail?.availableSpaces !== null) {
                                        <tr>
                                          <th scope="row">Available Spaces</th>
                                          <td>{{detail?.availableSpaces}}</td>
                                        </tr>
                                      }
                                      @if (detail?.sensorAssignment?.chipId) {
                                        <tr>
                                          <th scope="row">Sensor Chip</th>
                                          <td>{{detail?.sensorAssignment?.chipId}}</td>
                                        </tr>
                                      }
                                      @if (detail?.sensorAssignment?.dateInstalled) {
                                        <tr>
                                          <th scope="row">Sensor Installed</th>
                                          <td>{{formatTimestamp(detail?.sensorAssignment?.dateInstalled)}}</td>
                                        </tr>
                                      }
                                      @if (detail?.portAssignment?.chipId) {
                                        <tr>
                                          <th scope="row">Guidance Light Chip</th>
                                          <td>{{detail?.portAssignment?.chipId}}</td>
                                        </tr>
                                      }
                                      @if (detail?.portAssignment?.portNumber) {
                                        <tr>
                                          <th scope="row">Port Number</th>
                                          <td>{{detail?.portAssignment?.portNumber}}</td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                } @else {
                                  <small class="text-muted">No additional parking space details available.</small>
                                }
                              }
                            </div>
                          }
                        </div>
                      }
                      <!-- Guidance Light Details -->
                      @if (selectedDevice.DeviceType === 'GuidanceLight') {
                        <div class="info-section">
                          <button
                            type="button"
                            class="info-section-header"
                            (click)="toggleConfigCollapsed()"
                            [attr.aria-expanded]="!configCollapsed"
                            aria-controls="guidance-config-body"
                            >
                            <h4><i class="fas fa-lightbulb"></i> Guidance Light Configuration</h4>
                            <i class="fas" [ngClass]="configCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                          </button>
                          @if (!configCollapsed) {
                            <div id="guidance-config-body" class="info-section-body">
                              @if (isLoadingDeviceDetail) {
                                <div class="loading-indicator">
                                  <i class="fas fa-spinner fa-spin"></i> Loading guidance light information...
                                </div>
                              }
                              @if (!isLoadingDeviceDetail) {
                                @if (guidanceLightDetail; as detail) {
                                  <table class="device-config-table">
                                    <tbody>
                                      @if (detail?.mode) {
                                        <tr>
                                          <th scope="row">Mode</th>
                                          <td>{{detail?.mode}}</td>
                                        </tr>
                                      }
                                      @if (detail?.brightness) {
                                        <tr>
                                          <th scope="row">Brightness</th>
                                          <td>{{detail?.brightness}}</td>
                                        </tr>
                                      }
                                      @if (detail?.vacantColor) {
                                        <tr>
                                          <th scope="row">Vacant Color</th>
                                          <td>{{detail?.vacantColor}}</td>
                                        </tr>
                                      }
                                      @if (detail?.reservedColor) {
                                        <tr>
                                          <th scope="row">Reserved Color</th>
                                          <td>{{detail?.reservedColor}}</td>
                                        </tr>
                                      }
                                      @if (detail?.overrideOccupiedColor) {
                                        <tr>
                                          <th scope="row">Override Occupied</th>
                                          <td>{{detail?.overrideOccupiedColor}}</td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                  @if (detail?.portAssignments?.length) {
                                    <div class="port-connections">
                                      <div class="port-connections-header">Port Connections</div>
                                      <div class="port-tag-list">
                                        @for (port of detail?.portAssignments; track port) {
                                          <div class="port-tag">
                                            <span class="port-tag-port">Port {{port?.portNumber}}</span>
                                            <span class="port-tag-space">{{port?.parkingSpaceName || 'Unassigned'}}</span>
                                          </div>
                                        }
                                      </div>
                                    </div>
                                  }
                                  @if (!(detail?.portAssignments?.length)) {
                                    <div class="no-data">
                                      <small class="text-muted">No port assignments configured.</small>
                                    </div>
                                  }
                                } @else {
                                  <small class="text-muted">No additional guidance light details available.</small>
                                }
                              }
                            </div>
                          }
                        </div>
                      }
                      <!-- Sign Details -->
                      @if (selectedDevice.DeviceType === 'Sign') {
                        <div class="info-section">
                          <button
                            type="button"
                            class="info-section-header"
                            (click)="toggleSignConfigCollapsed()"
                            [attr.aria-expanded]="!signConfigCollapsed"
                            aria-controls="sign-config-body"
                            >
                            <h4><i class="fas fa-sign"></i> Sign Configuration</h4>
                            <i class="fas" [ngClass]="signConfigCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                          </button>
                          @if (!signConfigCollapsed) {
                            <div id="sign-config-body" class="info-section-body">
                              @if (isLoadingDeviceDetail) {
                                <div class="loading-indicator">
                                  <i class="fas fa-spinner fa-spin"></i> Loading sign information...
                                </div>
                              }
                              @if (!isLoadingDeviceDetail) {
                                @if (signDetail; as detail) {
                                  <table class="device-config-table">
                                    <tbody>
                                      @if (detail?.mode) {
                                        <tr>
                                          <th scope="row">Mode</th>
                                          <td>{{detail?.mode}}</td>
                                        </tr>
                                      }
                                      @if (detail?.type) {
                                        <tr>
                                          <th scope="row">Type</th>
                                          <td>{{detail?.type}}</td>
                                        </tr>
                                      }
                                      @if (detail?.displayAngle !== undefined && detail?.displayAngle !== null) {
                                        <tr>
                                          <th scope="row">Display Angle</th>
                                          <td>{{detail?.displayAngle}}</td>
                                        </tr>
                                      }
                                      @if (detail?.dayBrightness !== undefined && detail?.dayBrightness !== null) {
                                        <tr>
                                          <th scope="row">Day Brightness</th>
                                          <td>{{detail?.dayBrightness}}</td>
                                        </tr>
                                      }
                                      @if (detail?.nightBrightness !== undefined && detail?.nightBrightness !== null) {
                                        <tr>
                                          <th scope="row">Night Brightness</th>
                                          <td>{{detail?.nightBrightness}}</td>
                                        </tr>
                                      }
                                      @if (detail?.displayRuleSetId) {
                                        <tr>
                                          <th scope="row">Display Rule Set</th>
                                          <td>{{detail?.displayRuleSetId}}</td>
                                        </tr>
                                      }
                                      @if (detail?.targetStructureId) {
                                        <tr>
                                          <th scope="row">Target Structure</th>
                                          <td>{{detail?.targetStructureId}}</td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                  <!-- Sign Target Structure Totals -->
                                  @if (detail?.targetStructureId) {
                                    <div class="target-structure-totals">
                                      <h5><i class="fas fa-parking"></i> Target Structure Summary</h5>
                                      <div class="info-grid">
                                        <div class="info-item">
                                          <label><i class="fas fa-hashtag"></i> Total Spaces</label>
                                          <span class="total-count">{{detail?.totalSpaces || 0}}</span>
                                        </div>
                                        <div class="info-item">
                                          <label><i class="fas fa-circle" style="color: #28a745;"></i> Available</label>
                                          <span class="total-count available-count">{{detail?.availableSpaces || 0}}</span>
                                        </div>
                                        <div class="info-item">
                                          <label><i class="fas fa-circle" style="color: #dc3545;"></i> Occupied</label>
                                          <span class="total-count occupied-count">{{detail?.occupiedSpaces || 0}}</span>
                                        </div>
                                        <div class="info-item">
                                          <label><i class="fas fa-eye"></i> Target Spaces</label>
                                          <span class="total-count">{{(detail?.targetParkingSpaceIds || []).length}}</span>
                                        </div>
                                      </div>
                                      <!-- Highlight Status -->
                                      @if ((detail?.targetParkingSpaceIds || []).length > 0) {
                                        <div class="highlight-status">
                                          <p class="highlight-info">
                                            <i class="fas fa-lightbulb" style="color: #9B59B6;"></i>
                                            Target spaces filled with <strong style="color: #9B59B6;">purple color</strong>
                                          </p>
                                        </div>
                                      }
                                    </div>
                                    } <!-- close target-structure-totals -->
                                  } @else {
                                    <small class="text-muted">No additional sign details available.</small>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                        <!-- Gateway Details -->
                        @if (selectedDevice.DeviceType === 'Gateway') {
                          <div class="info-section">
                            <button
                              type="button"
                              class="info-section-header"
                              (click)="toggleGatewayConfigCollapsed()"
                              [attr.aria-expanded]="!gatewayConfigCollapsed"
                              aria-controls="gateway-config-body"
                              >
                              <h4><i class="fas fa-network-wired"></i> Gateway Configuration</h4>
                              <i class="fas" [ngClass]="gatewayConfigCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                            </button>
                            @if (!gatewayConfigCollapsed) {
                              <div id="gateway-config-body" class="info-section-body">
                                @if (isLoadingDeviceDetail) {
                                  <div class="loading-indicator">
                                    <i class="fas fa-spinner fa-spin"></i> Loading gateway information...
                                  </div>
                                }
                                @if (!isLoadingDeviceDetail) {
                                  @if (gatewayDetail; as detail) {
                                    <table class="device-config-table">
                                      <tbody>
                                        @if (detail?.secondsBetweenPolling !== undefined && detail?.secondsBetweenPolling !== null) {
                                          <tr>
                                            <th scope="row">Polling Interval (s)</th>
                                            <td>{{detail?.secondsBetweenPolling}}</td>
                                          </tr>
                                        }
                                        @if (detail?.frogInfringementDownsampling !== undefined && detail?.frogInfringementDownsampling !== null) {
                                          <tr>
                                            <th scope="row">Infringement Downsampling</th>
                                            <td>{{detail?.frogInfringementDownsampling}}</td>
                                          </tr>
                                        }
                                        @if (detail?.frogLightDownsampling !== undefined && detail?.frogLightDownsampling !== null) {
                                          <tr>
                                            <th scope="row">Light Downsampling</th>
                                            <td>{{detail?.frogLightDownsampling}}</td>
                                          </tr>
                                        }
                                        @if (detail?.frogSignDownsampling !== undefined && detail?.frogSignDownsampling !== null) {
                                          <tr>
                                            <th scope="row">Sign Downsampling</th>
                                            <td>{{detail?.frogSignDownsampling}}</td>
                                          </tr>
                                        }
                                        @if (detail?.enableRssiLogging !== undefined && detail?.enableRssiLogging !== null) {
                                          <tr>
                                            <th scope="row">RSSI Logging</th>
                                            <td>{{detail?.enableRssiLogging ? 'Enabled' : 'Disabled'}}</td>
                                          </tr>
                                        }
                                        @if (detail?.apn) {
                                          <tr>
                                            <th scope="row">APN</th>
                                            <td>{{detail?.apn}}</td>
                                          </tr>
                                        }
                                      </tbody>
                                    </table>
                                  } @else {
                                    <small class="text-muted">No additional gateway details available.</small>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                        <!-- Car Counter Details -->
                        @if (selectedDevice.DeviceType === 'CarCounter') {
                          <div class="info-section">
                            <button
                              type="button"
                              class="info-section-header"
                              (click)="toggleCarCounterConfigCollapsed()"
                              [attr.aria-expanded]="!carCounterConfigCollapsed"
                              aria-controls="car-counter-config-body"
                              >
                              <h4><i class="fas fa-tachometer-alt"></i> Car Counter</h4>
                              <i class="fas" [ngClass]="carCounterConfigCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                            </button>
                            @if (!carCounterConfigCollapsed) {
                              <div id="car-counter-config-body" class="info-section-body">
                                @if (isLoadingDeviceDetail) {
                                  <div class="loading-indicator">
                                    <i class="fas fa-spinner fa-spin"></i> Loading car counter information...
                                  </div>
                                }
                                @if (!isLoadingDeviceDetail) {
                                  @if (carCounterDetail; as detail) {
                                    @if ((detail | keyvalue)?.length) {
                                      <table class="device-config-table">
                                        <tbody>
                                          @for (entry of detail | keyvalue; track entry) {
                                            <tr>
                                              <th scope="row">{{entry.key}}</th>
                                              <td>{{entry.value}}</td>
                                            </tr>
                                          }
                                        </tbody>
                                      </table>
                                    } @else {
                                      <small class="text-muted">No additional car counter details available.</small>
                                    }
                                  } @else {
                                    <small class="text-muted">No car counter details returned.</small>
                                  }
                                }
                              </div>
                            }
                          </div>
                        }
                        <!-- Generic Device Data -->
                        @if (!isLoadingDeviceDetail && deviceDetail?.DeviceSpecificData && selectedDevice.DeviceType !== 'ParkingSpace' && selectedDevice.DeviceType !== 'GuidanceLight' && selectedDevice.DeviceType !== 'Sign' && selectedDevice.DeviceType !== 'Gateway' && selectedDevice.DeviceType !== 'CarCounter') {
                          <div class="info-section">
                            <button
                              type="button"
                              class="info-section-header"
                              (click)="toggleGenericConfigCollapsed()"
                              [attr.aria-expanded]="!genericConfigCollapsed"
                              aria-controls="generic-config-body"
                              >
                              <h4><i class="fas fa-wrench"></i> Device Details</h4>
                              <i class="fas" [ngClass]="genericConfigCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
                            </button>
                            @if (!genericConfigCollapsed) {
                              <div id="generic-config-body" class="info-section-body">
                                @if (deviceDetail?.DeviceSpecificData) {
                                  <div class="config-data">
                                    <pre>{{deviceDetail?.DeviceSpecificData | json}}</pre>
                                  </div>
                                }
                              </div>
                            }
                          </div>
                        }
                        <!-- Recent Messages -->
                        @if (selectedDevice.ChipId && ((deviceDetail?.RecentMessages && (deviceDetail?.RecentMessages?.length || 0) > 0) || isLoadingMessages)) {
                          <div class="info-section">
                            <h4><i class="fas fa-comments"></i> Recent Messages</h4>
                            @if (!isLoadingMessages && deviceDetail?.RecentMessages && (deviceDetail?.RecentMessages?.length || 0) > 0) {
                              <div class="messages-list">
                                @for (message of deviceDetail?.RecentMessages; track message; let i = $index) {
                                  <div class="message-card" [class.expanded]="i < 3">
                                    <div class="message-header">
                                      <span class="message-type">{{message.messageType}}</span>
                                      <span class="message-time">{{message.timestamp}}</span>
                                    </div>
                                    @if (i < 3) {
                                      <div class="message-payload">{{message.payloadSummary}}</div>
                                    }
                                  </div>
                                }
                                @if ((deviceDetail?.RecentMessages?.length || 0) > 3) {
                                  <div class="show-more">
                                    <small>+{{(deviceDetail?.RecentMessages?.length || 0) - 3}} more messages</small>
                                  </div>
                                }
                              </div>
                            }
                            @if (isLoadingMessages) {
                              <div class="loading-indicator">
                                <i class="fas fa-spinner fa-spin"></i> Loading recent messages...
                              </div>
                            }
                            @if (!isLoadingMessages && (!deviceDetail?.RecentMessages || (deviceDetail?.RecentMessages?.length || 0) === 0)) {
                              <div class="no-messages">
                                <small class="text-muted">No recent messages available</small>
                              </div>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>





